/**
 * Vista "In cancellazione" — parcheggio 48h delle mail da cancellare.
 *
 * Le mail qui elencate verranno cancellate DAVVERO dal server IMAP allo
 * scadere del countdown (auto-purge). L'utente puo':
 *  - Annullare (la mail torna nel feed);
 *  - Forzare subito la cancellazione con DOPPIA CONFERMA (primo click arma,
 *    secondo esegue), singola o in bulk.
 *
 * Grafica allineata agli altri elenchi (wm-table) con testata ROSSA:
 *  - hover sull'oggetto -> anteprima riassunto AI;
 *  - click sulla riga -> dettaglio messaggio a destra (MailViewer);
 *  - ordinamento colonne (default "Cestinata il" decrescente);
 *  - paginazione a blocchi da 30 (o dimensione scelta dall'utente).
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  Loader2,
  Sparkles,
  Trash2,
  Undo2,
  X,
} from 'lucide-react';
import { deletionApi } from '../../lib/api';
import { MailViewer } from '../layout/MailViewer';
import { useMailStore } from '../../store/mail';
import { HelpHint } from '../ui/HelpHint';
import { Tooltip } from '../ui/Tooltip';

export interface ParkedMail {
  id: string;
  message_id: string;
  uid: number;
  subject: string | null;
  from_address: string | null;
  marked_at: string;
  purge_after: string;
  status: 'parked' | 'confirmed';
  // Arricchimenti dal messaggio collegato (per hover e dettaglio)
  ai_summary?: string | null;
  snippet?: string | null;
  received_at?: string | null;
}

type SortKey = 'subject' | 'from_address' | 'marked_at' | 'purge_after';

// Countdown leggibile verso la scadenza (poi il worker cancella davvero).
function useCountdown(target: string): { label: string; expired: boolean } {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = new Date(target).getTime() - now;
  if (diff <= 0) return { label: 'In cancellazione automatica…', expired: true };
  const h = Math.floor(diff / 3_600_000);
  const m = Math.floor((diff % 3_600_000) / 60_000);
  const s = Math.floor((diff % 60_000) / 1000);
  return { label: `${h}h ${m}m ${s}s`, expired: false };
}

function ParkingRow({
  item,
  active,
  selected,
  onToggle,
  onOpen,
  onChanged,
}: {
  item: ParkedMail;
  active: boolean;
  selected: boolean;
  onToggle: (id: string) => void;
  onOpen: () => void;
  onChanged: () => void;
}) {
  const { label, expired } = useCountdown(item.purge_after);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const preview = item.ai_summary ?? item.snippet ?? null;

  // DOPPIA CONFERMA: primo click arma il pulsante, secondo esegue davvero.
  async function handleForce() {
    if (!confirming) {
      setConfirming(true);
      return;
    }
    setBusy(true);
    try {
      await deletionApi.confirm([item.id]);
      onChanged();
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  }

  async function handleCancel() {
    setBusy(true);
    try {
      await deletionApi.cancel([item.id]);
      onChanged();
    } finally {
      setBusy(false);
    }
  }

  return (
    <tr className={'wm-row group' + (active ? ' is-active' : '') + (selected ? ' is-selected' : '')}>
      {/* Checkbox */}
      <td onClick={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          checked={selected}
          onChange={() => onToggle(item.id)}
          aria-label="Seleziona"
        />
      </td>

      {/* Oggetto + anteprima riassunto AI su hover */}
      <td onClick={onOpen} className="relative cursor-pointer">
        <span className="block truncate">{item.subject ?? '(nessun oggetto)'}</span>
        {preview && (
          <div className="pointer-events-none absolute left-0 top-full z-30 mt-1 w-[380px] max-w-[90vw] opacity-0 transition-opacity duration-150 group-hover:opacity-100">
            <div className="wm-preview p-3">
              <div
                className="mb-1 flex items-center gap-1.5 text-[9px] font-semibold uppercase tracking-wider"
                style={{ color: 'var(--accent)' }}
              >
                <Sparkles size={9} /> {item.ai_summary ? 'Riassunto AI' : 'Anteprima'}
              </div>
              <p className="line-clamp-4 text-[12px] leading-relaxed" style={{ color: 'var(--text)' }}>
                {preview}
              </p>
            </div>
          </div>
        )}
      </td>

      {/* Mittente */}
      <td onClick={onOpen} className="cursor-pointer" style={{ color: 'var(--muted)' }}>
        <span className="block truncate">{item.from_address ?? '—'}</span>
      </td>

      {/* Cestinata il */}
      <td onClick={onOpen} className="cursor-pointer whitespace-nowrap" style={{ color: 'var(--muted)' }}>
        {new Date(item.marked_at).toLocaleString('it-IT')}
      </td>

      {/* Countdown */}
      <td
        onClick={onOpen}
        className="cursor-pointer whitespace-nowrap font-mono"
        style={{ color: expired ? 'var(--ko)' : 'var(--warn)' }}
      >
        {label}
      </td>

      {/* Azioni */}
      <td onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2">
          <button
            onClick={handleCancel}
            disabled={busy}
            className="wm-abtn flex items-center gap-1"
            title="Annulla: la mail resta e torna nel feed"
          >
            <Undo2 size={12} /> Annulla
          </button>
          <button
            onClick={handleForce}
            disabled={busy}
            className={
              confirming
                ? 'rounded bg-red-600 px-2 py-1 text-xs font-semibold text-white hover:bg-red-500'
                : 'wm-abtn wm-abtn-ko'
            }
            title="Cancella subito dal server (doppia conferma)"
          >
            {busy ? (
              <Loader2 size={12} className="animate-spin" />
            ) : confirming ? (
              'Sei sicuro? Elimina ora'
            ) : (
              'Forza cancellazione'
            )}
          </button>
          {confirming && (
            <button
              onClick={() => setConfirming(false)}
              className="text-xs hover:underline"
              style={{ color: 'var(--muted)' }}
            >
              annulla azione
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

export function DeletionParkingView() {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkConfirming, setBulkConfirming] = useState(false);

  // Dettaglio a destra: riusa lo store mail + MailViewer come gli altri elenchi.
  const selectedMessageId = useMailStore((s) => s.selectedMessageId);
  const setSelectedMessage = useMailStore((s) => s.setSelectedMessage);
  // Chiude un eventuale dettaglio rimasto aperto da un'altra vista.
  useEffect(() => {
    setSelectedMessage(null);
    return () => setSelectedMessage(null);
  }, [setSelectedMessage]);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['deletion-parked'],
    queryFn: () => deletionApi.parked() as Promise<{ items: ParkedMail[] }>,
    refetchInterval: 30_000,
  });
  const items = data?.items ?? [];

  // Ordinamento colonne: default "Cestinata il" decrescente (ultima cestinata in alto).
  const [sortKey, setSortKey] = useState<SortKey>('marked_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      // Le colonne data partono decrescenti (piu recente in alto), i testi crescenti.
      setSortDir(key === 'marked_at' || key === 'purge_after' ? 'desc' : 'asc');
    }
  }

  const sortedItems = useMemo(() => {
    const arr = [...items];
    arr.sort((a, b) => {
      const cmp =
        sortKey === 'marked_at' || sortKey === 'purge_after'
          ? new Date(a[sortKey]).getTime() - new Date(b[sortKey]).getTime()
          : (a[sortKey] ?? '').localeCompare(b[sortKey] ?? '', 'it', { sensitivity: 'base' });
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [items, sortKey, sortDir]);

  // Paginazione: default blocchi da 30, dimensione scelta dall'utente (0 = tutte).
  const [pageSize, setPageSize] = useState(30);
  const [page, setPage] = useState(0);
  const totalPages = pageSize > 0 ? Math.max(1, Math.ceil(sortedItems.length / pageSize)) : 1;
  const safePage = Math.min(page, totalPages - 1);
  const pagedItems = useMemo(
    () =>
      pageSize > 0 ? sortedItems.slice(safePage * pageSize, (safePage + 1) * pageSize) : sortedItems,
    [sortedItems, safePage, pageSize],
  );

  // Selezione di massa: pagina corrente o intero elenco.
  const allPageSelected = pagedItems.length > 0 && pagedItems.every((i) => selected.has(i.id));
  function togglePageSelection() {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allPageSelected) pagedItems.forEach((i) => next.delete(i.id));
      else pagedItems.forEach((i) => next.add(i.id));
      return next;
    });
  }
  function selectAll() {
    setSelected(new Set(sortedItems.map((i) => i.id)));
  }

  // Dopo annulla/forza: ricarica parcheggio, badge e liste messaggi.
  function onChanged() {
    void refetch();
    void queryClient.invalidateQueries({ queryKey: ['deletion-alert'] });
    void queryClient.invalidateQueries({ queryKey: ['messages'] });
    void queryClient.invalidateQueries({ queryKey: ['feed'] });
  }

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const selectedIds = useMemo(() => Array.from(selected), [selected]);

  async function handleBulkForce() {
    if (selectedIds.length === 0) return;
    if (!bulkConfirming) {
      setBulkConfirming(true);
      return;
    }
    await deletionApi.confirm(selectedIds);
    setSelected(new Set());
    setBulkConfirming(false);
    onChanged();
  }

  async function handleBulkCancel() {
    if (selectedIds.length === 0) return;
    await deletionApi.cancel(selectedIds);
    setSelected(new Set());
    onChanged();
  }

  function SortTh({ label, k, width }: { label: string; k: SortKey; width?: number }) {
    const active = sortKey === k;
    return (
      <th style={width ? { width } : undefined}>
        <button
          type="button"
          onClick={() => toggleSort(k)}
          className="inline-flex items-center gap-1 text-inherit"
          title={`Ordina per ${label}`}
        >
          {label}
          {active && (sortDir === 'asc' ? <ArrowUp size={11} /> : <ArrowDown size={11} />)}
        </button>
      </th>
    );
  }

  return (
    <div className="flex flex-1 overflow-hidden">
      {/* ---------- Elenco (stile wm, testata rossa) ---------- */}
      <div className="wm-list flex flex-1 flex-col overflow-hidden">
        <div className="shrink-0 px-6 pt-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="flex items-center gap-2 text-xl font-semibold" style={{ color: 'var(--text)' }}>
                <Trash2 size={18} style={{ color: 'var(--ko)' }} /> In cancellazione
                {/* Aiuto contestuale della sezione */}
                <HelpHint topic="deletion" />
              </h1>
              <p className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white">
                <AlertTriangle size={14} />
                Queste mail verranno cancellate DEFINITIVAMENTE dal server allo scadere del
                countdown (48h). Puoi annullare o forzare la cancellazione.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleBulkCancel}
                disabled={selectedIds.length === 0}
                className="wm-abtn disabled:opacity-40"
              >
                Annulla selezionate ({selectedIds.length})
              </button>
              <button
                onClick={handleBulkForce}
                disabled={selectedIds.length === 0}
                className={
                  bulkConfirming
                    ? 'rounded bg-red-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-red-500'
                    : 'wm-abtn wm-abtn-ko disabled:opacity-40'
                }
              >
                {bulkConfirming
                  ? `Conferma definitiva (${selectedIds.length})`
                  : `Forza cancellazione (${selectedIds.length})`}
              </button>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-auto px-6 pb-4 pt-4">
          {isLoading ? (
            <p style={{ color: 'var(--muted)' }}>Caricamento…</p>
          ) : items.length === 0 ? (
            <p style={{ color: 'var(--muted)' }}>Nessuna mail in cancellazione.</p>
          ) : (
            <table
              className="wm-table wm-table--danger border-separate"
              style={{ borderSpacing: 0, tableLayout: 'fixed', width: '100%', minWidth: 900 }}
            >
              <thead>
                <tr>
                  <th style={{ width: 40 }}>
                    <input
                      type="checkbox"
                      aria-label="Seleziona pagina"
                      checked={allPageSelected}
                      onChange={togglePageSelection}
                      title="Seleziona/deseleziona la pagina corrente"
                    />
                  </th>
                  <SortTh label="Oggetto" k="subject" />
                  <SortTh label="Mittente" k="from_address" width={220} />
                  <SortTh label="Cestinata il" k="marked_at" width={150} />
                  <SortTh label="Cancellazione tra" k="purge_after" width={160} />
                  <th style={{ width: 260 }}>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {pagedItems.map((item) => (
                  <ParkingRow
                    key={item.id}
                    item={item}
                    active={selectedMessageId === item.message_id}
                    selected={selected.has(item.id)}
                    onToggle={toggle}
                    onOpen={() => setSelectedMessage(item.message_id)}
                    onChanged={onChanged}
                  />
                ))}
              </tbody>
            </table>
          )}

          {/* ---------- Selezione totale + paginazione ---------- */}
          {items.length > 0 && (
            <div
              className="mt-3 flex flex-wrap items-center justify-between gap-3 text-xs"
              style={{ color: 'var(--muted)' }}
            >
              <div className="flex items-center gap-3">
                <button onClick={selectAll} className="wm-abtn" title="Seleziona tutte le mail dell'elenco, anche nelle altre pagine">
                  Seleziona tutte ({sortedItems.length})
                </button>
                {selectedIds.length > 0 && (
                  <button onClick={() => setSelected(new Set())} className="hover:underline">
                    Deseleziona ({selectedIds.length})
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span>Per pagina:</span>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(0);
                  }}
                  className="wm-abtn"
                  style={{ color: 'var(--text)' }}
                >
                  <option value={30}>30</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={0}>Tutte</option>
                </select>
                {pageSize > 0 && totalPages > 1 && (
                  <>
                    {/* Freccia icona-only: tooltip descrittivo */}
                    <Tooltip label="Pagina precedente" side="top">
                      <button
                        onClick={() => setPage(Math.max(0, safePage - 1))}
                        disabled={safePage === 0}
                        className="wm-abtn disabled:opacity-40"
                      >
                        &#8249;
                      </button>
                    </Tooltip>
                    <span>
                      Pagina {safePage + 1} di {totalPages}
                    </span>
                    {/* Freccia icona-only: tooltip descrittivo */}
                    <Tooltip label="Pagina successiva" side="top">
                      <button
                        onClick={() => setPage(Math.min(totalPages - 1, safePage + 1))}
                        disabled={safePage >= totalPages - 1}
                        className="wm-abtn disabled:opacity-40"
                      >
                        &#8250;
                      </button>
                    </Tooltip>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ---------- Dettaglio messaggio a destra (come gli altri elenchi) ---------- */}
      {selectedMessageId && (
        <aside
          className="wm-list relative flex w-[560px] shrink-0 flex-col border-l"
          style={{ borderColor: 'var(--border)' }}
        >
          <div
            className="flex h-9 shrink-0 items-center justify-between border-b px-3"
            style={{ borderColor: 'var(--border)', background: 'var(--card)' }}
          >
            <button className="wm-btn" onClick={() => setSelectedMessage(null)} title="Chiudi">
              <ArrowLeft size={14} /> Torna all'elenco
            </button>
            {/* Pulsante icona-only: tooltip descrittivo */}
            <Tooltip label="Chiudi" side="bottom">
              <button
                onClick={() => setSelectedMessage(null)}
                title="Chiudi"
                className="rounded-md p-1"
                style={{ color: 'var(--muted)' }}
              >
                <X size={16} />
              </button>
            </Tooltip>
          </div>
          <div className="flex flex-1 overflow-hidden">
            <MailViewer />
          </div>
        </aside>
      )}
    </div>
  );
}
